import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { cn } from '../../utils/cn'
import { footerVariants } from './filterAnimation'

// Half the slider thumb's width: the thumb centre stops this far from each end of the track
const THUMB_INSET = 4

const rangeLettersClassName =
  'relative top-[-2rem] inline-block text-[2.5rem] tracking-[0.02em] text-[rgba(255,255,255,0.72)] [transition:opacity_0.4s_ease,transform_0.4s_ease]'

const letterClassName =
  'pointer-events-none whitespace-nowrap leading-none tracking-[0.04em] [text-shadow:0_0_4px_rgba(0,0,0,0.5)]'
const smallLetterClassName = cn(letterClassName, 'text-[2.5rem] text-grey40')
const activeLetterClassName = cn(letterClassName, 'text-[4rem] text-red')

// the range input: its end dots, the dashed track and the striped thumb (WebKit and Firefox pseudo-elements)
const progressInputClassName = [
  "w-full flex-1 cursor-pointer appearance-none bg-transparent [-webkit-appearance:none] before:absolute before:left-0 before:top-[10px] before:h-[10px] before:w-[10px] before:rounded-[100%] before:bg-white80 before:content-[''] after:absolute after:right-0 after:top-[10px] after:h-[10px] after:w-[10px] after:rounded-[100%] after:bg-white80 after:content-['']",
  '[&::-moz-range-track]:h-[2px] [&::-moz-range-track]:border-none [&::-moz-range-track]:bg-[length:6px_2px] [&::-moz-range-track]:bg-[position:left_center] [&::-moz-range-track]:bg-repeat-x [&::-moz-range-track]:[background-image:linear-gradient(_to_right,rgba(255,255,255,0.7)_0px,rgba(255,255,255,0.7)_2px,transparent_2px,transparent_6px_)] [&::-webkit-slider-runnable-track]:h-[2px] [&::-webkit-slider-runnable-track]:border-none [&::-webkit-slider-runnable-track]:bg-[length:6px_2px] [&::-webkit-slider-runnable-track]:bg-[position:left_center] [&::-webkit-slider-runnable-track]:bg-repeat-x [&::-webkit-slider-runnable-track]:[background-image:linear-gradient(_to_right,rgba(255,255,255,0.7)_0px,rgba(255,255,255,0.7)_2px,transparent_2px,transparent_6px_)]',
  '[&::-moz-range-thumb]:h-[26px] [&::-moz-range-thumb]:w-[9px] [&::-moz-range-thumb]:rounded-[1px] [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:bg-red [&::-moz-range-thumb]:bg-[length:5px_16px] [&::-moz-range-thumb]:bg-center [&::-moz-range-thumb]:bg-no-repeat [&::-moz-range-thumb]:[background-image:repeating-linear-gradient(_to_right,rgba(255,255,255,0.9)_0,rgba(255,255,255,0.9)_1px,transparent_1px,transparent_2px_)] [&::-webkit-slider-thumb]:mt-[-12px] [&::-webkit-slider-thumb]:h-[26px] [&::-webkit-slider-thumb]:w-[9px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-[1px] [&::-webkit-slider-thumb]:border-none [&::-webkit-slider-thumb]:bg-red [&::-webkit-slider-thumb]:bg-[length:5px_16px] [&::-webkit-slider-thumb]:bg-center [&::-webkit-slider-thumb]:bg-no-repeat [&::-webkit-slider-thumb]:[-webkit-appearance:none] [&::-webkit-slider-thumb]:[background-image:repeating-linear-gradient(_to_right,rgba(255,255,255,0.9)_0,rgba(255,255,255,0.9)_1px,transparent_1px,transparent_2px_)]',
].join(' ')

interface KeywordsFilterFooterProps {
  listRef: React.RefObject<HTMLDivElement | null>
  letters: string[]
  keywordCountRange: { minCount: number; maxCount: number }
}

const KeywordsFilterFooter = ({
  listRef,
  letters,
  keywordCountRange,
}: KeywordsFilterFooterProps) => {
  const { t } = useTranslation()
  const [scrollProgress, setScrollProgress] = useState(0)
  const [activeLetter, setActiveLetter] = useState('A')
  const [beforeLetter, setBeforeLetter] = useState<string | null>(null)
  const [afterLetter, setAfterLetter] = useState<string | null>(null)
  const [isAtEnd, setIsAtEnd] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const progressWrapRef = useRef<HTMLDivElement>(null)
  const dragOffsetRef = useRef(0)

  const updateScrollState = useCallback(() => {
    const list = listRef.current
    if (!list) return

    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth)
    const progress = maxScroll > 0 ? (list.scrollLeft / maxScroll) * 100 : 0

    const viewportCenter = list.scrollLeft + list.clientWidth / 2
    let activeIndex = 0

    // The list's direct children are the keyword columns, in order
    Array.from(list.children).forEach((child, index) => {
      const column = child as HTMLElement
      const columnCenter = column.offsetLeft + column.clientWidth / 2
      if (columnCenter <= viewportCenter) {
        activeIndex = index
      }
    })

    // If scrolled to the very end, always surface the last letter
    const atEnd = maxScroll > 0 && list.scrollLeft >= maxScroll - 5
    const actualIndex = atEnd ? letters.length - 1 : activeIndex
    const currentLetter = atEnd
      ? letters[letters.length - 1] || 'A'
      : letters[activeIndex] || letters[0] || 'A'

    setIsAtEnd(atEnd)
    setScrollProgress(atEnd ? 100 : progress)
    setActiveLetter(currentLetter)
    setBeforeLetter(actualIndex > 0 ? letters[actualIndex - 1] : null)
    setAfterLetter(actualIndex < letters.length - 1 ? letters[actualIndex + 1] : null)
  }, [listRef, letters])

  // Reset the indicator whenever the letter set changes (e.g. language switch)
  useEffect(() => {
    setActiveLetter(letters[0] || 'A')
    setBeforeLetter(null)
    setAfterLetter(null)
    setIsAtEnd(false)
    setScrollProgress(0)
  }, [letters])

  // Highlight the active letter's column. Flagged on the DOM node directly so the
  // memoized keyword columns don't re-render on scroll.
  useEffect(() => {
    const list = listRef.current
    if (!list) return

    const activeIndex = letters.indexOf(activeLetter)
    Array.from(list.children).forEach((child, index) => {
      child.toggleAttribute('data-active', index === activeIndex)
    })
  }, [listRef, letters, activeLetter])

  // Subscribe to the list's scroll directly so a scroll only re-renders the footer,
  // not the full keyword list. rAF-throttled to at most one update per frame.
  useEffect(() => {
    const list = listRef.current
    if (!list) return

    let frame = 0
    const handleScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        updateScrollState()
      })
    }

    list.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      list.removeEventListener('scroll', handleScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [listRef, updateScrollState])

  const scrollToProgress = (nextProgress: number) => {
    setScrollProgress(nextProgress)

    const list = listRef.current
    if (!list) return

    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth)
    list.scrollLeft = (nextProgress / 100) * maxScroll
    updateScrollState()
  }

  const handleProgressChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    scrollToProgress(Number(e.target.value))
  }

  // The floating letters act as a larger grab area for the slider thumb.
  // Mirrors the thumb geometry used by FloatingLetterWrap's `left` calc.
  const getThumbTravel = () => {
    const wrap = progressWrapRef.current
    if (!wrap) return null
    const rect = wrap.getBoundingClientRect()
    return { start: rect.left + THUMB_INSET, length: rect.width - THUMB_INSET * 2 }
  }

  const handleLetterPointerDown: React.PointerEventHandler<HTMLSpanElement> = e => {
    const travel = getThumbTravel()
    if (!travel) return

    // Remember where inside the letters the grab happened so the thumb doesn't jump
    const thumbX = travel.start + (travel.length * scrollProgress) / 100
    dragOffsetRef.current = e.clientX - thumbX
    e.currentTarget.setPointerCapture(e.pointerId)
    setIsDragging(true)
    e.preventDefault()
  }

  const handleLetterPointerMove: React.PointerEventHandler<HTMLSpanElement> = e => {
    if (!isDragging) return
    const travel = getThumbTravel()
    if (!travel || travel.length <= 0) return

    const ratio = (e.clientX - dragOffsetRef.current - travel.start) / travel.length
    scrollToProgress(Math.min(100, Math.max(0, ratio * 100)))
  }

  const handleLetterPointerEnd = () => setIsDragging(false)

  const progressStyle = {
    ['--progress' as const]: scrollProgress / 100,
  } as React.CSSProperties

  // on xxl the language changer, the filter bar and the brand logo float 6vh above the bottom edge
  // (70px tall), so the track is lifted above them and inset to clear the motif counter button
  // (112px wide, 3vw + 6px from the left edge)
  // it slides up following the overlay's variant
  return (
    <motion.div
      className="fixed bottom-0 left-0 z-[6] flex w-screen items-center gap-[12px] px-[6rem] pb-[4rem] pt-[0rem] max-sm:gap-[8px] max-sm:px-[12px] max-sm:pb-[12px] max-sm:pt-[8px] xxl:px-[calc(3vw+118px+2rem)] xxl:pb-[calc(6vh+70px+2rem)]"
      variants={footerVariants}
    >
      <span
        className="min-w-[8rem] whitespace-nowrap text-[0.9rem] tracking-[0.02em] text-[rgba(255,255,255,0.72)]"
        hidden
      >
        min: {keywordCountRange.minCount} | max: {keywordCountRange.maxCount}
      </span>
      <span
        className={cn(rangeLettersClassName, {
          'opacity-0 [transform:scale(0.5)]': activeLetter === letters[0],
          'opacity-100 [transform:scale(1)]': activeLetter !== letters[0],
        })}
      >
        {t('keywordsFilter.rangeLettersFirst')}
      </span>
      <div className="relative flex-1" ref={progressWrapRef} style={progressStyle}>
        <span
          className={cn(
            'absolute left-[calc((100%-8px)*var(--progress)+4px)] top-[-4rem] inline-flex touch-none select-none items-baseline gap-[0.3rem] [-webkit-user-select:none] [transform:translateX(-50%)]',
            { 'cursor-grabbing': isDragging, 'cursor-grab': !isDragging },
          )}
          onPointerDown={handleLetterPointerDown}
          onPointerMove={handleLetterPointerMove}
          onPointerUp={handleLetterPointerEnd}
          onPointerCancel={handleLetterPointerEnd}
          onLostPointerCapture={handleLetterPointerEnd}
        >
          {beforeLetter && <span className={smallLetterClassName}>{beforeLetter}</span>}
          {isAtEnd ? (
            <span className={activeLetterClassName}>{activeLetter}</span>
          ) : (
            <>
              <span className={activeLetterClassName}>{activeLetter}</span>
              {afterLetter && <span className={smallLetterClassName}>{afterLetter}</span>}
            </>
          )}
        </span>
        <input
          className={progressInputClassName}
          type="range"
          min={0}
          max={100}
          step={0.1}
          value={scrollProgress}
          onChange={handleProgressChange}
          aria-label="Keyword letter progress"
        />
      </div>
      <span
        className={cn(rangeLettersClassName, {
          'opacity-0 [transform:scale(0.5)]': activeLetter === letters[letters.length - 1],
          'opacity-100 [transform:scale(1)]': activeLetter !== letters[letters.length - 1],
        })}
      >
        {t('keywordsFilter.rangeLettersLast')}
      </span>
    </motion.div>
  )
}

export default React.memo(KeywordsFilterFooter)
